import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";

import { QueryClientProvider } from "@oneapp/data";
import { queryClient } from "./lib/queryClient";
import { ThemeProvider } from "@repo/tokens";
import { resolveInitialTheme } from "./lib/resolveInitialTheme.ts";
import { ToastProvider } from "@repo/ui";
import { initSentry } from "./sentry.ts";
import { initializeApi } from "./lib/setupApi";
import { initLsqSqsEvents } from "./lib/initLsqSqsEvents";
import { useFeatureStore } from "./store/useFeatureStore";
import "./i18n";
import { AgoraProvider } from "./pages/agora/context-api/index.tsx";
import { initAnalytics } from "./lib/setupAnalytics";
import {
  parseLink,
  parseSignupReferralDeeplink,
} from "./components/deeplink/parseLink";
import {
  parseGeniusDeeplink,
  normalizeGeniusDeeplinkLocation,
  redirectToGeniusDeeplinkIfNeeded,
  redirectToPersistedGeniusDeeplinkIfNeeded,
} from "./components/deeplink/genius/parseGeniusDeeplink";
import {
  abandonGeniusDeeplinkIfAuthEscape,
  isGeniusAppShellPath,
  isGeniusAuthEscapePath,
  isGeniusFlowInProgress,
  persistGeniusDeeplinkParams,
  prepareGeniusDeeplinkPageLoad,
  readPersistedGeniusDeeplinkParams,
  readPersistedGeniusOriginalUrl,
  shouldRedirectBaseUrlToGeniusDeeplink,
  shouldEnforceGeniusBackTrap,
  hasGeniusShellResumeInThisTab,
  markGeniusShellResumeInThisTab,
  hasGeniusExpectResumeOnShell,
} from "./components/deeplink/genius/geniusDeeplinkSession";
import { isPathAllowedDuringGeniusNavLock } from "./components/deeplink/genius/geniusDeeplinkNavLock";
import {
  clearGeniusLogoutConfirmPending,
  requestGeniusLogoutConfirm,
} from "./components/deeplink/genius/geniusLogoutConfirm";
import { syncGeniusDeeplinkBackTrap } from "./components/deeplink/genius/geniusDeeplinkBackTrap";
import { useAuthStore } from "./store/useAuthStore";
import { registerChunkLoadRecovery } from "./lib/chunkLoadRecovery";
import { bootstrapInfinityLearnHandoff } from "./lib/infinityLearnHandoff";

function restoreDocumentVisibility(): void {
  try {
    document.documentElement.style.visibility = "";
    if (document.body) document.body.style.visibility = "";
  } catch {
    // ignore
  }
}

// Prior blank-leave set visibility:hidden; bfcache / same-tab return must not
// leave a permanent white page.
restoreDocumentVisibility();
window.addEventListener("pageshow", () => {
  restoreDocumentVisibility();
});

initSentry();
registerChunkLoadRecovery();
initializeApi();
initLsqSqsEvents();
initAnalytics();
useFeatureStore.getState().init();

const WEB_DEEPLINK_URL_STORAGE_KEY = "web_deeplink_url";

/**
 * @returns true when a hard navigation away from this document was started
 * (do not mount React — it would race and bounce to the blank leave page).
 */
const captureInitialDeeplinkUrl = (): boolean => {
  try {
    const incomingUrl = window.location.href;
    const incomingGeniusParams = parseGeniusDeeplink(incomingUrl);

    // Keep the inbound URL (including UTMs) before Genius rewrites the path.
    if (incomingGeniusParams) {
      persistGeniusDeeplinkParams(incomingGeniusParams, incomingUrl);
      normalizeGeniusDeeplinkLocation();
    }

    const hrefAfterNormalize = window.location.href;
    const geniusParams = parseGeniusDeeplink(hrefAfterNormalize);
    if (geniusParams) {
      const persisted = readPersistedGeniusDeeplinkParams();
      if (
        hasGeniusExpectResumeOnShell() &&
        persisted?.signedToken === geniusParams.signedToken
      ) {
        markGeniusShellResumeInThisTab();
      }
      const isShellResume = hasGeniusShellResumeInThisTab();
      prepareGeniusDeeplinkPageLoad(geniusParams, {
        isShellResumeEntry: isShellResume,
      });
      clearGeniusLogoutConfirmPending();
      persistGeniusDeeplinkParams(geniusParams, hrefAfterNormalize);
      redirectToGeniusDeeplinkIfNeeded();
      // Arm Back → logout confirm BEFORE React mounts so the first Back works
      // even if the user presses it during welcome/auth bootstrap.
      syncGeniusDeeplinkBackTrap(
        shouldEnforceGeniusBackTrap(useAuthStore.getState().profilesData),
      );
      return false;
    }

    if (shouldRedirectBaseUrlToGeniusDeeplink()) {
      const pathname = window.location.pathname;
      const allowed = isPathAllowedDuringGeniusNavLock(pathname);
      const shouldArmBackTrap = shouldEnforceGeniusBackTrap(
        useAuthStore.getState().profilesData,
      );

      if (allowed) {
        // Refresh/resume on an allowed Genius screen — arm trap before React.
        syncGeniusDeeplinkBackTrap(shouldArmBackTrap);
        return false;
      }

      // Login/intro/signup — never blank or force Genius; clear sticky lock.
      if (isGeniusAuthEscapePath(pathname)) {
        abandonGeniusDeeplinkIfAuthEscape(pathname);
        return false;
      }

      // Base / shell URL while OTP pending → ALWAYS resume Genius.
      if (isGeniusAppShellPath(pathname)) {
        markGeniusShellResumeInThisTab();
        const redirected = redirectToPersistedGeniusDeeplinkIfNeeded(
          readPersistedGeniusOriginalUrl(),
          readPersistedGeniusDeeplinkParams(),
        );
        // Hard redirect started — do not mount React on this document.
        if (redirected) return true;
        // Soft path: mount React and let the router resume Genius.
        return false;
      }

      // Address-bar edit to a non-shell, non-Genius path → logout popup on mount.
      // Skip while Genius is still bootstrapping (deeplink forward nav can pass
      // through disallowed intermediate routes).
      if (!isGeniusFlowInProgress()) {
        requestGeniusLogoutConfirm();
      }
      return false;
    }

    const isDeepLink =
      (parseLink(incomingUrl) !== null ||
        parseSignupReferralDeeplink(incomingUrl) !== null) &&
      !parseGeniusDeeplink(incomingUrl);
    if (!isDeepLink) return false;
    localStorage.setItem(WEB_DEEPLINK_URL_STORAGE_KEY, incomingUrl);
    return false;
  } catch {
    return false;
  }
};

function renderApp() {
  const rootEl = document.getElementById("root");
  if (!rootEl) return;

  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider initialTheme={resolveInitialTheme()}>
          <ToastProvider>
            <AgoraProvider>
              <App />
            </AgoraProvider>
          </ToastProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

async function bootstrapAndRender() {
  if (captureInitialDeeplinkUrl()) {
    return;
  }

  const isGeniusEntry = parseGeniusDeeplink(window.location.href) != null;
  const geniusSessionPending = shouldRedirectBaseUrlToGeniusDeeplink();

  if (geniusSessionPending) {
    syncGeniusDeeplinkBackTrap(
      shouldEnforceGeniusBackTrap(useAuthStore.getState().profilesData),
    );
  }

  // Genius signed-token auth / pending Genius resume must not await IL handoff —
  // it can hang forever and leave a white #root.
  if (!isGeniusEntry && !geniusSessionPending) {
    try {
      await Promise.race([
        bootstrapInfinityLearnHandoff(),
        new Promise<never>((_, reject) => {
          window.setTimeout(
            () => reject(new Error("IL handoff timed out")),
            4_000,
          );
        }),
      ]);
    } catch (error) {
      console.warn("[IL Handoff] Bootstrap failed:", error);
    }
  }

  renderApp();
}

void bootstrapAndRender();
